// The Rosary & Scapular card in several languages, matching the languages the Rosary reader
// offers (English + Latin, Spanish, French) plus Arabic and Chinese. Spanish/French prayer
// wording is reused from the reader; Arabic and Chinese use traditional Catholic forms.
import { SPANISH_PRAYERS } from "./rosaryPrayers.es";
import { FRENCH_PRAYERS } from "./rosaryPrayers.fr";
import { ROSARY_SCAPULAR_CARD } from "./rosaryScapularCard";
import { SCAPULAR_SOURCES } from "./scapular";

const IMAGE = ROSARY_SCAPULAR_CARD.image;
const SOURCE = ROSARY_SCAPULAR_CARD.source;
const FOOTER = "Theotokos · Mater Dei — Ad Iesum per Mariam";

const L = {
  es: {
    label: "Español",
    eyebrow: "Para imprimir y llevar · Español",
    title: "El Santo Rosario y el Escapulario del Carmen",
    subtitle: "Las oraciones esenciales, el orden de las cuentas y el signo carmelita de Nuestra Señora",
    howToLabel: "Cómo rezar el Rosario",
    howTo: "En el crucifijo: la Señal de la Cruz y el Credo. En la primera cuenta, el Padrenuestro; en las tres siguientes, un Avemaría; luego el Gloria. En cada uno de los cinco misterios: se anuncia, y se reza un Padrenuestro, diez Avemarías, un Gloria y la Oración de Fátima. Se concluye con la Salve.",
    prayers: [SPANISH_PRAYERS.our_father, SPANISH_PRAYERS.hail_mary, SPANISH_PRAYERS.glory, SPANISH_PRAYERS.fatima],
    scapularLabel: "El Escapulario del Carmen",
    scapular: "Por el Rosario y el Escapulario ella salvará al mundo — paráfrasis de un dicho devocional tradicional, de atribución no verificada. El Escapulario es un sacramental, no un amuleto ni una garantía automática: signo de pertenencia a María y de seguimiento de su Hijo. Pide a un sacerdote la bendición e imposición de la Iglesia.",
    note: "La oración en los labios y la fidelidad en la vida van unidas. Esta tarjeta reúne oraciones católicas tradicionales; la reflexión sobre el Escapulario es opcional y no añade cuentas ni oraciones obligatorias al Rosario.",
    back: { eyebrow: "Bajo su manto", name: "Nuestra Señora del Rosario · del Carmen", blessing: "Reina del Santísimo Rosario, ruega por nosotros.\nMadre del Carmelo, revístenos del manto de la gracia y guarda a esta familia bajo tu amparo." },
  },
  fr: {
    label: "Français",
    eyebrow: "À imprimer et à porter · Français",
    title: "Le Saint Rosaire et le Scapulaire brun",
    subtitle: "Les prières essentielles, l’ordre du chapelet et le signe carmélite de Notre-Dame",
    howToLabel: "Comment prier le Rosaire",
    howTo: "Sur le crucifix : le Signe de la Croix et le Credo. Sur le premier grain, le Notre Père ; sur les trois suivants, un Je vous salue Marie ; puis le Gloire au Père. Pour chacun des cinq mystères : on l’annonce, puis un Notre Père, dix Je vous salue Marie, un Gloire au Père et la Prière de Fatima. On termine par le Salve Regina.",
    prayers: [FRENCH_PRAYERS.our_father, FRENCH_PRAYERS.hail_mary, FRENCH_PRAYERS.glory, FRENCH_PRAYERS.fatima],
    scapularLabel: "Le Scapulaire du Carmel",
    scapular: "Par le Rosaire et le Scapulaire elle sauvera le monde — paraphrase d’une parole de dévotion traditionnelle, d’attribution non vérifiée. Le Scapulaire est un sacramental, non un porte-bonheur ni une garantie automatique : signe d’appartenance à Marie et de la suite de son Fils. Demandez à un prêtre la bénédiction et l’imposition de l’Église.",
    note: "La prière sur les lèvres et la fidélité dans la vie vont ensemble. Cette carte rassemble des prières catholiques traditionnelles ; la réflexion sur le Scapulaire est facultative et n’ajoute ni grain ni prière obligatoire au Rosaire.",
    back: { eyebrow: "Sous son manteau", name: "Notre-Dame du Rosaire · du Mont-Carmel", blessing: "Reine du très saint Rosaire, priez pour nous.\nMère du Carmel, revêtez-nous du manteau de la grâce et gardez cette famille sous votre protection." },
  },
  ar: {
    label: "العربية",
    dir: "rtl",
    eyebrow: "للطباعة والحمل · العربية",
    title: "المسبحة المقدّسة وثوب جبل الكرمل",
    subtitle: "الصلوات الأساسية، وترتيب الحبّات، وعلامة سيّدتنا الكرمليّة",
    howToLabel: "كيف تُصلّى المسبحة",
    howTo: "على الصليب: إشارة الصليب وقانون الإيمان. على الحبّة الأولى: الأبانا؛ وعلى الثلاث التالية: السلام عليك يا مريم؛ ثمّ المجد. ولكلّ سرٍّ من الأسرار الخمسة: يُعلَن، ثمّ يُصلّى أبانا، وعشر مرّات السلام عليك يا مريم، والمجد، وصلاة فاطيما. ويُختَم بالسلام يا ملكة.",
    prayers: [
      { title: "أبانا", text: "أبانا الّذي في السماوات، ليتقدّس اسمُك، ليأتِ ملكوتُك، لتكن مشيئتُك كما في السماء كذلك على الأرض. أعطِنا خبزَنا كفاف يومِنا، واغفر لنا خطايانا كما نغفر نحن لمن أخطأ إلينا، ولا تُدخلنا في التجربة، لكن نجّنا من الشرّير. آمين." },
      { title: "السلام عليك يا مريم", text: "السلام عليك يا مريم، يا ممتلئةً نعمة، الربّ معك، مباركةٌ أنتِ في النساء، ومباركةٌ ثمرةُ بطنِك يسوع. يا قدّيسة مريم، يا والدة الله، صلّي لأجلنا نحن الخطأة، الآن وفي ساعة موتنا. آمين." },
      { title: "المجد", text: "المجد للآب والابن والروح القدس، كما كان في البدء والآن وكلّ أوان وإلى دهر الدهور. آمين." },
      { title: "صلاة فاطيما", text: "يا يسوعي، اغفر لنا خطايانا، ونجّنا من نار جهنّم، واهدِ إلى السماء جميع النفوس، لا سيّما المحتاجة إلى رحمتك." },
    ],
    scapularLabel: "ثوب جبل الكرمل",
    scapular: "بالمسبحة والثوب (الإسكابولير) تُخلّص العالم — صياغة بتصرّف لقولٍ تعبّديٍّ تقليديٍّ لم تُتحقَّق نسبتُه. الإسكابولير علامةٌ مقدّسة، لا تعويذةٌ ولا ضمانٌ تلقائي: علامةُ انتماءٍ لمريم واتّباعٍ لابنها. اطلب من الكاهن بركة الكنيسة وإلباسه.",
    note: "الصلاة على الشفاه والأمانة في الحياة متلازمتان. تجمع هذه البطاقة صلواتٍ كاثوليكيّةً تقليديّة؛ والتأمّل في الإسكابولير اختياريٌّ ولا يُضيف حبّاتٍ أو صلواتٍ إلزاميّةً إلى المسبحة.",
    back: { eyebrow: "في حماها", name: "سيّدة الوردية · سيّدة الكرمل", blessing: "يا ملكة الوردية المقدّسة، صلّي لأجلنا.\nيا أمّ الكرمل، ألبِسينا ثوب النعمة، واحفظي هذه العائلة في حِمى سترِك." },
  },
  zh: {
    label: "中文",
    eyebrow: "可打印随身携带 · 中文",
    title: "圣玫瑰经与加尔默罗圣衣",
    subtitle: "基本经文、念珠的次序，以及圣母的加尔默罗标记",
    howToLabel: "如何念玫瑰经",
    howTo: "在十字架上：划十字圣号，念宗徒信经。第一颗珠念天主经；接下来三颗各念一遍圣母经；再念圣三光荣经。每一端（共五端）：先报明奥迹，然后念一遍天主经、十遍圣母经、一遍圣三光荣经和花地玛祷词。最后念又圣母经。",
    prayers: [
      { title: "天主经", text: "我们的天父，愿你的名受显扬；愿你的国来临；愿你的旨意奉行在人间，如同在天上。求你今天赏给我们日用的食粮；求你宽恕我们的罪过，如同我们宽恕别人一样；不要让我们陷于诱惑；但救我们免于凶恶。阿们。" },
      { title: "圣母经", text: "万福玛利亚，你充满圣宠，主与你同在，你在妇女中受赞颂，你的亲子耶稣同受赞颂。天主圣母玛利亚，求你现在和我们临终时，为我们罪人祈求天主。阿们。" },
      { title: "圣三光荣经", text: "愿光荣归于父、及子、及圣神。起初怎样，今日亦然，直到永远。阿们。" },
      { title: "花地玛祷词", text: "我耶稣，请宽恕我们的罪过，救我们于地狱的永火，并求你把众人的灵魂，特别是那些最需要你仁慈的灵魂，领到天国。" },
    ],
    scapularLabel: "加尔默罗圣衣",
    scapular: "藉着玫瑰经与圣衣，她将拯救世界——这是传统敬礼说法的意译，出处未经证实，并非经核实的直接引文。圣衣是圣仪，并非护身符或自动的保证：它是归属圣母、追随其圣子的标记。请向司铎请求教会的祝福与授衣礼。",
    note: "口中的祈祷与生活中的忠信应当并行。此卡汇集传统的天主教经文；关于圣衣的省思是可选的，并不为玫瑰经增添任何念珠或必念的经文。",
    back: { eyebrow: "在她的荫护下", name: "玫瑰圣母 · 加尔默罗圣母", blessing: "至圣玫瑰之后，为我们祈求。\n加尔默罗之母，以圣宠之衣覆庇我们，护佑这个家庭常在你的荫护之下。" },
  },
};

const localeCard = (code) => {
  const l = L[code];
  const dir = l.dir;
  return {
    duplexSplit: 2,
    lang: code,
    eyebrow: l.eyebrow,
    title: l.title,
    subtitle: l.subtitle,
    image: IMAGE,
    blocks: [
      { label: l.howToLabel, text: l.howTo, dir },
      ...l.prayers.map((p) => ({ label: p.title, text: p.text, dir })),
      { label: l.scapularLabel, text: l.scapular, dir },
    ],
    note: l.note,
    source: SOURCE,
    back: { ...l.back, blessing: l.prayers[1].text, dir },
    footer: FOOTER,
  };
};

export const ROSARY_CARD_LANGS = [
  { code: "en", label: "English & Latin" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "ar", label: "العربية" },
  { code: "zh", label: "中文" },
];

export const buildRosaryScapularCard = (code = "en") =>
  code === "en" ? ROSARY_SCAPULAR_CARD : localeCard(code);
